import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {AccessType, ConnectionStatus, Role, ViewportTab} from '@app/modules/shared/data/constants';
import produce from 'immer';
import {User} from '@app/models/vcr-entity/user';
import {UtilService} from '@app/modules/shared/services/util.service';
import {RemoteUserAction, RemoteUserInfoStateModel} from "@app/state/remote-users.state";

export class LocalUser {
  public id: number;
  public identity: string;
  public name: string;
  public nickname: string;
  public firstName: string;
  public lastName: string;
  public email: string;
  public accessType: AccessType;
  public displayName: string;
  public role: Role;
  public isGuest: boolean;
  public personalColor: string;

  public hasUnansweredMessage: boolean;
  public isCurrentClassParticipant: boolean;
  public roomConnectionStatus: ConnectionStatus;
  public isInFullscreenViewMode: boolean;
  public isHandRaised: boolean;
  public audio: { isMuted: boolean; isProvided: boolean; selfMuteAttemptsCount: number };
  public video: { isMuted: boolean; isProvided: boolean };
  public currentSelectedTab: ViewportTab;
  public lateTimeInMinutes: number;
  public stickers: string [];
  public correctAnswersCount: number;

  constructor(userEntity?: User) {
    if (userEntity) {
      this.id = userEntity.id;
      this.identity = userEntity.identity;
      this.name = userEntity.name;
      this.nickname = userEntity.nickname;
      this.firstName = userEntity.firstName;
      this.lastName = userEntity.lastName;
      this.email = userEntity.email;
      this.accessType = userEntity.accessType;
      this.displayName = UtilService.generateDisplayName(userEntity);
      this.role = userEntity.role;
      this.isGuest = userEntity.isGuest;
      this.personalColor = userEntity.personalColor;

      this.hasUnansweredMessage = false;
      this.roomConnectionStatus = ConnectionStatus.ONLINE;
      this.isInFullscreenViewMode = false;
      this.isHandRaised = false;
      this.audio = { isMuted: false, isProvided: false, selfMuteAttemptsCount: 0 };
      this.video = { isMuted: false, isProvided: false };
      this.currentSelectedTab = ViewportTab.WHITEBOARD;
      this.lateTimeInMinutes = 0;
      this.stickers = [];
    }
  }
}

export namespace LocalUserAction {

  export class SetLocalUser {
    static readonly type = '[Local-user] set user state';

    constructor(public user: LocalUser) {
    }
  }

  export class SetLocalUserAccessType {
    static readonly type = '[Local-user] set user role';

    constructor(public accessType: AccessType) {
    }
  }

  export class SetLocalUserIdentity {
    static readonly type = '[Local-user] set user identity';

    constructor(public identity: string) {
    }
  }

  export class SetIsCurrentClassParticipant {
    static readonly type = '[Local-user] set is current class participant';

    constructor(public isCurrentClassParticipant: boolean) {
    }
  }

  export class SetIsVideoMuteState {
    static readonly type = '[Current-user-video-info] set user track mute';

    constructor(public isMuted: boolean) {
    }
  }

  export class SetIsAudioProvided {
    static readonly type = '[Current-user-audio-info] set user track provided';

    constructor(public isProvided: boolean) {
    }
  }

  export class SetIsVideoProvided {
    static readonly type = '[Current-user-video-info] set user track provided';

    constructor(public isProvided: boolean) {
    }
  }

  export class SetIsAudioMuteState {
    static readonly type = '[Current-user-audio-info] set user track mute state.';

    constructor(public isMuted: boolean) {
    }
  }

  export class SetIsSelfMutedCount {
    static readonly type = '[Current-user-audio-info] set user self mute count';

    constructor(public count: number) {
    }
  }

  export class SetLocalUserRaiseHand {
    static readonly type = '[Current-user-rise-hand] set user rise hand';

    constructor(public isRisen: boolean) {
    }
  }

  export class SetCurrentTab {
    static readonly type = '[Local-user-current-tab] set user current tab';

    constructor(public tabIndex: ViewportTab) {
    }
  }

  export class SetCurrentUserRiseHandCancel {
    static readonly type = '[Current-user-rise-hand-cancel] set user rise hand cancel';

    constructor(public isRisen: boolean) {
    }
  }

  export class SetHasUnansweredMessage {
    static readonly type = '[Current-user-has-unanswered message] set user has unanswered message';

    constructor(public hasUnansweredMessage: boolean) {
    }
  }

  export class SetLateTime {
    static readonly type = '[Current-user-audio-info] set user late time';

    constructor(public lateTime: number) {
    }
  }

  export class AddSticker {
    static readonly type = '[Current-user] add sticker';

    constructor(public sticker: string) {
    }
  }

  export class SetStickers {
    static readonly type = '[Current-user] set stickers';

    constructor(public stickers: string[]) {
    }
  }

  export class SetNickname {
    static readonly type = '[Current-user] set nickname';

    constructor(public nickname: string) {
    }
  }

  export class RemoveSticker {
    static readonly type = '[Current-user] remove sticker';

    constructor() {}
  }

  export class SetCorrectAnswer {
    static readonly type = '[Current-user] set correct answers count';

    constructor(public score: number) {}
  }
}

@State<LocalUser>({
  name: LocalUserState.storeName,
  defaults: LocalUserState.defaults
})
@Injectable()
export class LocalUserState {
  static readonly storeName = 'currentUser';
  static readonly defaults: LocalUser = {
    id: -1,
    identity: '',
    name: '',
    nickname: '',
    firstName: '',
    lastName: '',
    email: '',
    accessType: null,
    displayName: '',
    role: null,
    isGuest: false,
    personalColor: '#000',

    hasUnansweredMessage: false,
    isCurrentClassParticipant: true,
    roomConnectionStatus: ConnectionStatus.OFFLINE,
    isInFullscreenViewMode: false,
    isHandRaised: false,
    audio: {isMuted: false, isProvided: false, selfMuteAttemptsCount: 0},
    video: {isMuted: false, isProvided: false},
    currentSelectedTab: ViewportTab.WHITEBOARD,
    lateTimeInMinutes: -1,
    stickers: [],
    correctAnswersCount: 0
  };

  @Selector()
  static getLocalUser(user: LocalUser) {
    return user;
  }

  @Selector()
  static getIsCurrentUserAudioProvided(state: LocalUser) {
    return state.audio.isProvided;
  }

  @Action(LocalUserAction.SetLocalUser)
  setLocalUser({setState}: StateContext<LocalUser>, event: LocalUserAction.SetLocalUser) {
    setState(event.user);
  }

  @Action(LocalUserAction.SetLocalUserAccessType)
  setLocalUserAccessType({patchState}: StateContext<LocalUser>, event: LocalUserAction.SetLocalUserAccessType) {
    patchState({accessType: event.accessType});
  }

  @Action(LocalUserAction.SetLocalUserIdentity)
  setLocalUserIdentity({patchState}: StateContext<LocalUser>, event: LocalUserAction.SetLocalUserIdentity) {
    patchState({identity: event.identity});
  }

  @Action(LocalUserAction.SetIsCurrentClassParticipant)
  setIsCurrentClassParticipant({patchState}: StateContext<LocalUser>, {isCurrentClassParticipant}: LocalUserAction.SetIsCurrentClassParticipant) {
    patchState({isCurrentClassParticipant});
  }

  @Action(LocalUserAction.SetIsSelfMutedCount)
  setCurrentUserSelfMutedCountState(ctx: StateContext<LocalUser>, info: LocalUserAction.SetIsSelfMutedCount) {
    const state = ctx.getState();

    ctx.patchState({
      audio: {isMuted: state.audio.isMuted, isProvided: state.audio.isProvided, selfMuteAttemptsCount: info.count}
    });
  }

  @Action(LocalUserAction.SetLateTime)
  setCurrentUserLateTime(ctx: StateContext<LocalUser>, info: LocalUserAction.SetLateTime) {
    ctx.patchState({
      lateTimeInMinutes: info.lateTime
    });
  }

  @Action(LocalUserAction.SetLocalUserRaiseHand)
  toggleRaiseHand(ctx: StateContext<LocalUser>, info: LocalUserAction.SetLocalUserRaiseHand) {
    ctx.setState(produce((state) => {
      state.isHandRaised = info.isRisen;
    }));
  }

  @Action(LocalUserAction.SetCurrentTab)
  setCurrentTab(ctx: StateContext<LocalUser>, info: LocalUserAction.SetCurrentTab) {
    ctx.patchState({
      currentSelectedTab: info.tabIndex
    });
  }

  @Action(LocalUserAction.AddSticker)
  addStickerToCurrentUser(ctx: StateContext<LocalUser>, info: LocalUserAction.AddSticker) {
    ctx.setState(produce((state) => {
      if (state.stickers.length > 10) {
        state.stickers.splice(0, 1);
      }
      state.stickers.push(info.sticker);
    }));
  }

  @Action(LocalUserAction.SetStickers)
  setLocalUserStickers(ctx: StateContext<LocalUser>, {stickers}: LocalUserAction.SetStickers) {
    ctx.patchState({
      stickers
    });
  }

  @Action(LocalUserAction.SetNickname)
  setNickname(ctx: StateContext<LocalUser>, info: LocalUserAction.SetNickname) {
    ctx.patchState({
      nickname: info.nickname
    });
  }

  @Action(LocalUserAction.RemoveSticker)
  removeStickerFromCurrentUser(ctx: StateContext<LocalUser>, info: LocalUserAction.RemoveSticker) {
    ctx.setState(produce((state) => {
      state.stickers.pop();
    }));
  }

  @Action(LocalUserAction.SetHasUnansweredMessage)
  setHasUnansweredMessage(ctx: StateContext<LocalUser>, {hasUnansweredMessage}: LocalUserAction.SetHasUnansweredMessage) {
    ctx.setState(produce((state) => {
      state.hasUnansweredMessage = hasUnansweredMessage;
    }));
  }

  @Action(LocalUserAction.SetIsVideoMuteState)
  setCurrentUserVideoMuteState(ctx: StateContext<LocalUser>, info: LocalUserAction.SetIsVideoMuteState) {
    const state = ctx.getState();
    ctx.patchState({
      video: {...state.video, isMuted: info.isMuted}
    });
  }

  @Action(LocalUserAction.SetIsAudioProvided)
  setCurrentUserAudioProvided(ctx: StateContext<LocalUser>, info: LocalUserAction.SetIsAudioProvided) {
    const state = ctx.getState();
    ctx.patchState({
      audio: {...state.audio, isProvided: info.isProvided}
    });
  }

  @Action(LocalUserAction.SetIsVideoProvided)
  setCurrentUserVideoProvided(ctx: StateContext<LocalUser>, info: LocalUserAction.SetIsVideoProvided) {
    const state = ctx.getState();
    ctx.patchState({
      video: {...state.video, isProvided: info.isProvided}
    });
  }

  @Action(LocalUserAction.SetIsAudioMuteState)
  setCurrentUserAudioMuteState(ctx: StateContext<LocalUser>, info: LocalUserAction.SetIsAudioMuteState) {
    const state = ctx.getState();
    ctx.patchState({
      audio: {...state.audio, isMuted: info.isMuted}
    });
  }

  @Action(LocalUserAction.SetCorrectAnswer)
  setCorrectAnswers(ctx: StateContext<LocalUser>, {score}: LocalUserAction.SetCorrectAnswer) {
    ctx.patchState({
      correctAnswersCount: score
    });
  }
}



